import { ChevronDoubleDownIcon, ChevronDoubleUpIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getduplicateLoans } from 'services'
import { Pagination, Tooltip } from 'stories/components'
import { getLoanDisplayNo, thousandSeperator } from 'utils'
import { renderSortButton } from 'utils/table'

export interface IDuplicateLoansData {
  headers: string[]
  pipelineHeaders: string[][]
  data: any[][]
  additionalData: Array<{
    no: number
    byteproFileName?: number | string
    thirdPartyId?: string
    entityTitle?: string
    rush?: boolean | string
    servicingPipeline?: boolean | string
    condsByCategory?: Record<string, { all: number; cleared: number }>
    tags?: string[]
    /** Why this loan is related: same subject property, same entity, and/or the same person. */
    matchReasons?: RelatedLoanMatch[]
    /** Servicing has this loan Paid in Full. Only ever true while paid-off loans are shown. */
    isPaidOff?: boolean
  }>
  total: number
  /** Matches on the subject property address, counted across every match rather than this page. */
  potentialDuplicateCount?: number
  /**
   * Related loans that are paid off, counted across every match whether or not they are being
   * shown - it is the number the "show paid off" toggle offers to bring in.
   */
  paidOffCount?: number
  totalLoanAmount: number
}

interface IProps {
  loanNumber: string
  duplicateLoans: IDuplicateLoansData
  onDuplicateLoansChange: (data: IDuplicateLoansData | null) => void
  /** Whether paid-off related loans are being listed. Off by default; the toggle here flips it. */
  includePaidOff: boolean
  onIncludePaidOffChange: (value: boolean) => void
}

export type RelatedLoanMatch = 'property' | 'entity' | 'borrower'

const MATCH_LABELS: Record<RelatedLoanMatch, { label: string; className: string }> = {
  property: { label: 'Same Property', className: 'bg-red-50 text-red-700 border-red-200' },
  entity: { label: 'Same Entity', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  borrower: { label: 'Same Borrower', className: 'bg-sky-50 text-sky-700 border-sky-200' },
}

// One chip metric for the match and tag chips so they sit on a shared wrapping row without the
// taller one setting the line height. Tight leading and 1px padding keep a chip line to ~16px.
const CHIP_CLASS = 'inline-flex items-center text-[10px] leading-[14px] px-1.5 py-[1px] whitespace-nowrap'

/** Tags past this are collapsed into a +N chip, so a heavily tagged loan cannot stretch its row. */
const MAX_VISIBLE_TAGS = 3

/**
 * Ceiling on the Loan # cell. Everything in it is on one row, so without a ceiling a loan with
 * three match chips and three tags would widen the column far enough to squeeze the columns that
 * carry the actual data. At this width the usual row still fits on one line and only a busy one
 * wraps to a second.
 */
const LOAN_CELL_MAX_WIDTH = 'max-w-[330px]'

/** A closed file, shown only when the reader asks for the paid-off loans. Deliberately quiet. */
const PAID_OFF_CHIP_CLASS = `${CHIP_CLASS} font-normal rounded border bg-slate-100 text-slate-600 border-slate-300`

const INITIAL_VIEW_COUNT = 2
const PAGE_SIZE = 10

export const DuplicateLoansTable = ({
  loanNumber,
  duplicateLoans,
  onDuplicateLoansChange,
  includePaidOff,
  onIncludePaidOffChange,
}: IProps) => {
  const {
    headers,
    pipelineHeaders,
    data,
    additionalData,
    total,
    potentialDuplicateCount = 0,
    paidOffCount = 0,
  } = duplicateLoans

  const [viewAll, setViewAll] = useState(false)
  const [pageNum, setPageNum] = useState(0)
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortDir, setSortDir] = useState<1 | -1>(1)

  const headerIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    headers.forEach((header: string, index: number) => {
      if (header) map.set(header.toLowerCase(), index)
    })
    return map
  }, [headers])

  const getHeaderIndex = (headerName: string): number => {
    return headerIndexMap.get(headerName?.toLowerCase()) ?? -1
  }

  // 'Tags' is only a column when an admin has put it in the Potential Duplicate Headers config.
  // When it isn't, the tags still show, stacked in the Loan # cell, so a duplicate's Mature / NSF
  // labels are visible without a per-tenant config change.
  const hasTagsColumn = headerIndexMap.has('tags')

  const headerColumns = useMemo(() => {
    if (!pipelineHeaders || pipelineHeaders.length === 0) return []

    const numColumns = pipelineHeaders[0].length
    const columns: string[][] = []

    for (let col = 0; col < numColumns; col++) {
      const columnHeaders: string[] = []
      for (let row = 0; row < pipelineHeaders.length; row++) {
        const value = pipelineHeaders[row]?.[col]
        if (value && value.trim() !== '') columnHeaders.push(value)
      }
      if (columnHeaders.length > 0) columns.push(columnHeaders)
    }

    return columns
  }, [pipelineHeaders])

  const sortedRows = useMemo(() => {
    const rows = data.map((row, idx) => ({ row, extra: additionalData[idx] ?? {} }))

    if (!sortBy) return rows

    let colIndex = -1
    if (sortBy === 'Loan #') colIndex = -2
    else colIndex = getHeaderIndex(sortBy)

    if (colIndex === -1) return rows

    const sorted = [...rows].sort((a, b) => {
      let aVal: any
      let bVal: any
      if (colIndex === -2) {
        aVal = getLoanDisplayNo(a.extra)
        bVal = getLoanDisplayNo(b.extra)
      } else {
        aVal = a.row[colIndex]
        bVal = b.row[colIndex]
      }

      const aEmpty = aVal === null || aVal === undefined || aVal === ''
      const bEmpty = bVal === null || bVal === undefined || bVal === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDir
      }

      return String(aVal).localeCompare(String(bVal)) * sortDir
    })

    return sorted
  }, [data, additionalData, sortBy, sortDir, headerIndexMap])

  // `withPaidOff` is passed explicitly by the toggle, which has to fetch with the value it is
  // switching TO rather than the one still in state.
  const fetchDuplicates = async (skip: number, count: number, withPaidOff: boolean = includePaidOff) => {
    // View All / paging must not hit the API for an unsaved structure (/structure/new).
    if (!loanNumber || loanNumber.trim().toLowerCase() === 'new') return
    setLoading(true)
    try {
      const res: any = await getduplicateLoans(loanNumber, { skip, count, includePaidOff: withPaidOff })
      if (res.success && Array.isArray(res.data)) {
        onDuplicateLoansChange({
          headers: res.headers ?? [],
          pipelineHeaders: res.pipelineHeaders ?? [],
          data: res.data,
          additionalData: res.additionalData ?? [],
          total: res.total ?? res.data.length,
          potentialDuplicateCount: res.potentialDuplicateCount ?? 0,
          paidOffCount: res.paidOffCount ?? 0,
          totalLoanAmount: res.totalLoanAmount ?? 0,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const onToggleIncludePaidOff = async () => {
    const next = !includePaidOff
    onIncludePaidOffChange(next)
    setPageNum(0)
    await fetchDuplicates(0, viewAll ? PAGE_SIZE : INITIAL_VIEW_COUNT, next)
  }

  const onToggleViewAll = async () => {
    if (viewAll) {
      setPageNum(0)
      setViewAll(false)
      await fetchDuplicates(0, INITIAL_VIEW_COUNT)
      return
    }
    setPageNum(0)
    setViewAll(true)
    await fetchDuplicates(0, PAGE_SIZE)
  }

  const onPageNavigate = async (num: number) => {
    setPageNum(num)
    await fetchDuplicates(num * PAGE_SIZE, PAGE_SIZE)
  }

  const onSort = (title: string) => {
    if (!viewAll) {
      onToggleViewAll()
    }
    if (sortBy !== title) {
      setSortBy(title)
      setSortDir(1)
      return
    }
    if (sortDir === 1) {
      setSortDir(-1)
      return
    }
    setSortBy('')
    setSortDir(1)
  }

  const renderHeader = (title: string) => {
    const sortOrder = sortBy === title ? sortDir : 0
    return renderSortButton({
      title,
      sortOrder,
      onClick: () => onSort(title),
      btnClassName: 'items-center text-[11px]',
    })
  }

  // Read-only tag chips for an associated loan. Styled like the chips in <LoanTags />, but
  // deliberately not that component: this box lists OTHER loans, so their tags are shown rather
  // than edited, and its compact mode is a fixed 220px that doesn't fit this grid.
  //
  // Returns the chips themselves rather than a row, so the Loan # cell can sit them on the same
  // wrapping line as the match chips instead of starting a second one.
  const renderTagChips = (tags?: string[]) => {
    if (!tags?.length) return null
    const shown = tags.slice(0, MAX_VISIBLE_TAGS)
    const hidden = tags.slice(MAX_VISIBLE_TAGS)
    const tagClass = `${CHIP_CLASS} max-w-[140px] truncate rounded-full bg-shade-blue/10 font-medium text-shade-blue`
    return (
      <>
        {shown.map((tag) => (
          <span key={tag} className={tagClass}>
            {tag}
          </span>
        ))}
        {hidden.length > 0 && (
          <Tooltip message={hidden.join(', ')}>
            <span className={`${tagClass} cursor-pointer`}>+{hidden.length}</span>
          </Tooltip>
        )}
      </>
    )
  }

  const withBorder = (content: JSX.Element, hasBorder: boolean) => {
    return hasBorder ? <div className="border-b w-full mb-1">{content}</div> : <div>{content}</div>
  }

  const renderStatus = (row: any[], extra: Record<string, any>, hasBorder: boolean) => {
    const statusIndex = getHeaderIndex('Status')
    const loanStatus = statusIndex >= 0 ? row[statusIndex] : ''
    const condsByCategory = extra.condsByCategory
    const isLoanSetupOrOnHold = ['Loan Setup', 'On Hold', 'Application'].includes(loanStatus)

    return withBorder(
      <>
        <div>{loanStatus}</div>
        {condsByCategory && (
          <div className="flex flex-wrap gap-x-2">
            {Object.keys(condsByCategory).map((key, idx) => {
              let view = true
              if (key === 'Loan Intake') view = isLoanSetupOrOnHold
              if (isLoanSetupOrOnHold) view = key === 'Loan Intake'
              if (!view) return null
              return (
                <span className="text-[11px] mt-0.5" key={idx}>
                  {key}: {condsByCategory[key].cleared} / {condsByCategory[key].all}
                  {idx == 0 ? ',' : ''}
                </span>
              )
            })}
          </div>
        )}
      </>,
      hasBorder,
    )
  }

  const renderCellValue = (header: string, row: any[], extra: Record<string, any>, hasBorder: boolean) => {
    const headerLower = header?.toLowerCase() || ''

    if (headerLower === 'status') {
      return renderStatus(row, extra, hasBorder)
    }

    if (headerLower === 'tags') {
      const chips = renderTagChips(extra.tags)
      return withBorder(
        chips ? (
          <div className="flex flex-wrap items-center gap-1">{chips}</div>
        ) : (
          <span className="text-gray-400 text-[11px]">No tags</span>
        ),
        hasBorder,
      )
    }

    const dataIndex = getHeaderIndex(header)
    const value = dataIndex >= 0 ? row[dataIndex] : ''
    // Three decimals for the same reason the pipeline grid uses three: these are the same rows,
    // and a rate is quoted to the thousandth.
    const displayValue = typeof value === 'number' ? thousandSeperator(value, false, 3) : value

    return withBorder(<>{displayValue}</>, hasBorder)
  }

  // The headline counts what is on show, so with paid-off loans hidden it is the live ones. When
  // every related loan is paid off there is no table under it at all, and the line has to say what
  // the box is holding back rather than read as "no related loans".
  const countText = total
    ? `${total} other ${total === 1 ? 'loan' : 'loans'} sharing this borrower, entity or property`
    : `${paidOffCount} other ${
        paidOffCount === 1 ? 'loan' : 'loans'
      } sharing this borrower, entity or property, all paid off`

  const heading = (
    <div className="mb-1 ml-2 flex flex-wrap items-baseline gap-x-2">
      <span className="font-semibold text-gray-800">Related Loans</span>
      <span className="text-[11px] text-gray-500">{countText}</span>
      {paidOffCount > 0 && (
        <span className="btn-link !text-[11px]" onClick={onToggleIncludePaidOff}>
          {includePaidOff ? `Hide paid off (${paidOffCount})` : `Show paid off (${paidOffCount})`}
        </span>
      )}
    </div>
  )

  // Nothing left to put in a table once the paid-off loans are hidden. The heading stays so the
  // toggle is there to bring them back.
  if (!data.length) return <div className="mb-4">{heading}</div>

  return (
    <div className="mb-4">
      {heading}
      {potentialDuplicateCount > 0 && (
        <div className="text-red-700 font-semibold mb-1 ml-2">* Potential Duplicate File</div>
      )}
      <div className={`overflow-auto ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="w-fit overflow-hidden rounded-lg border border-slate-300">
          <table className="text-xs text-left text-gray-900 dark:text-gray-400">
            <thead className="text-[11px] text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="py-1.5 px-2 border">
                  {renderHeader('Loan #')}
                </th>
                {headerColumns.map((columnHeaders, colIndex) => (
                  <th scope="col" className="py-1.5 px-2 border" key={`dup-header-${colIndex}`}>
                    <span>
                      {columnHeaders.map((header, headerIndex) => (
                        <div key={`${colIndex}-${headerIndex}`}>
                          {headerIndex < columnHeaders.length - 1 ? (
                            <div className="border-b w-fit mb-1 border-gray-300">{renderHeader(header)}</div>
                          ) : (
                            <div>{renderHeader(header)}</div>
                          )}
                        </div>
                      ))}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-[12px] text-gray-900">
              {sortedRows.map(({ row, extra }, index) => {
                const loanNo = extra.no
                return (
                  <tr key={`dup-${loanNo}-${index}`} className={`border-b ${index % 2 ? 'bg-slate-50' : ''}`}>
                    <td className="border px-2 py-1">
                      {/* Everything this cell knows sits on ONE wrapping row: the loan number, the
                          servicer number, Rush, why the loan matched, and its tags. Stacking any of
                          it cost the row a line. LOAN_CELL_MAX_WIDTH is what makes the row wrap
                          instead of widening the column until it crowds out the real data. */}
                      <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${LOAN_CELL_MAX_WIDTH}`}>
                        <Link
                          to={`/loan_process/overview/${loanNo}`}
                          target="_blank"
                          className="font-bold text-shade-blue hover:underline cursor-pointer"
                        >
                          {getLoanDisplayNo({ ...extra, loanNumber: loanNo })}
                        </Link>
                        {extra.thirdPartyId && <span className="text-gray-700 text-[11px]">{extra.thirdPartyId}</span>}
                        {extra.rush && <span className="text-red-700 italic text-[11px]">Rush</span>}
                        {extra.isPaidOff && <span className={PAID_OFF_CHIP_CLASS}>Paid Off</span>}
                        {extra.matchReasons?.map((reason) => {
                          const match = MATCH_LABELS[reason]
                          if (!match) return null
                          return (
                            <span
                              className={`${CHIP_CLASS} font-normal rounded border ${match.className}`}
                              key={reason}
                            >
                              {match.label}
                            </span>
                          )
                        })}
                        {!hasTagsColumn && renderTagChips(extra.tags)}
                      </div>
                    </td>
                    {headerColumns.map((columnHeaders, colIndex) => (
                      <td className="border px-2 py-1" key={`dup-data-${index}-${colIndex}`}>
                        <span>
                          {columnHeaders.map((header, headerIndex) => {
                            const hasBorder = headerIndex < columnHeaders.length - 1
                            const isEntityCol = header?.toLowerCase() === 'borrower'
                            return (
                              <div key={`${colIndex}-${headerIndex}`}>
                                {isEntityCol && extra.entityTitle ? (
                                  <div className={hasBorder ? 'border-b w-full mb-1' : ''}>
                                    <div className="flex gap-2 items-center">
                                      <span>{row[getHeaderIndex(header)]}</span>
                                      <Tooltip message={extra.entityTitle}>
                                        <IdentificationIcon className="w-[13px] h-[13px] cursor-pointer" />
                                      </Tooltip>
                                    </div>
                                  </div>
                                ) : (
                                  renderCellValue(header, row, extra, hasBorder)
                                )}
                              </div>
                            )
                          })}
                        </span>
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
          {total > INITIAL_VIEW_COUNT && (
            <div className="flex items-center gap-2 mt-2 justify-end">
              <span className="btn-link !text-sm" onClick={onToggleViewAll}>
                {viewAll ? (
                  <span className="flex items-center px-2 py-1">
                    <ChevronDoubleUpIcon className="w-4 h-4 mr-1" />
                    View Less
                  </span>
                ) : (
                  <span className="flex items-center px-2 py-1">
                    <ChevronDoubleDownIcon className="w-4 h-4 mr-1" />
                    View All
                  </span>
                )}
              </span>
            </div>
          )}
          {viewAll && total > PAGE_SIZE && (
            <Pagination totalCount={total} itemCountPerPage={PAGE_SIZE} pageNum={pageNum} onNavigate={onPageNavigate} />
          )}
        </div>
      </div>
    </div>
  )
}
